/**
 * @file CrossfitCoupleDebutant.tsx
 * @summary Article de blog — CrossfitCoupleDebutant
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import BlogHeroImage, { getArticleImageUrls, withArticleImages, slugFromUrl } from "@/components/blog/BlogHeroImage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft, Heart, Users, Calendar, Sparkles } from "lucide-react";
import BlogCTA, { BlogStickyCTA } from "@/components/blog/BlogCTA";

const URL = "https://crossfitchistera.fr/blog/crossfit-couple-debutant";

const SLUG = slugFromUrl(URL);
const IMAGES = getArticleImageUrls(SLUG, 5);
const faq = [
  { "@type": "Question", name: "Peut-on s'inscrire en couple chez CrossFit Chistera ?", acceptedAnswer: { "@type": "Answer", text: "Oui. Vous pouvez réserver ensemble vos créneaux via Resawod et profiter des séances dans le même cours, accompagnés par le même coach." } },
  { "@type": "Question", name: "Faut-il avoir le même niveau ?", acceptedAnswer: { "@type": "Answer", text: "Non. Les séances sont scalables : chacun adapte la charge, l'amplitude et l'intensité à son niveau, dans le même WOD." } },
  { "@type": "Question", name: "Combien coûte un abonnement à deux ?", acceptedAnswer: { "@type": "Answer", text: "Chaque adhérent a son propre abonnement. Les formules vont de 64 €/mois (2 séances/semaine) à 90 €/mois (illimité). 2 séances d'essai sont gratuites." } },
  { "@type": "Question", name: "Le CrossFit, c'est vraiment pour les débutants ?", acceptedAnswer: { "@type": "Answer", text: "Oui. La majorité des nouveaux adhérents n'ont jamais fait de CrossFit. Les coachs enseignent les bases avant tout : posture, respiration, mouvements clés." } },
];

const jsonLd = [
  { "@context": "https://schema.org", "@type": "Article", headline: "Débuter le CrossFit en couple : leur retour d'expérience", description: "Comment un couple débutant a découvert le CrossFit chez Chistera et transformé sa routine sportive avec plaisir.", author: { "@type": "Organization", name: "CrossFit Chistera" }, publisher: { "@type": "Organization", name: "CrossFit Chistera" }, mainEntityOfPage: URL, inLanguage: "fr-FR" },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
];

const points = [
  { icon: Calendar, title: "Une routine partagée", desc: "Mêmes créneaux, même salle, même progression : un rituel à deux." },
  { icon: Users, title: "Une dynamique collective", desc: "Le groupe pousse, encourage, dédramatise. On ne s'isole pas." },
  { icon: Sparkles, title: "Du plaisir, pas du sacrifice", desc: "Des WODs courts, variés, jamais deux fois la même séance." },
  { icon: Heart, title: "Du temps de qualité", desc: "1h ensemble pour soi, sans téléphone, sans écran." },
];

export default function CrossfitCoupleDebutant() {
  return (
    <>
      <SEO
        title="Débuter le CrossFit en couple : leur retour d'expérience"
        description="Comment un couple débutant a découvert le CrossFit chez Chistera et transformé sa routine sportive avec plaisir."
        canonical={URL}
        jsonLd={withArticleImages(jsonLd, IMAGES)}
          ogImages={IMAGES}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-32 md:pb-20">
          <article className="container mx-auto px-4 max-w-4xl">
            <BlogHeroImage slug={SLUG} />
            <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-muted-foreground flex items-center flex-wrap gap-1">
              <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-3 h-3" /> Accueil</Link>
              <ChevronRight className="w-3 h-3" />
              <span>Blog</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground">CrossFit en couple</span>
            </nav>

            <header className="mb-10">
              <span className="inline-block text-xs font-semibold text-gold tracking-wider mb-3 font-body normal-case uppercase">Témoignage · Lecture ~ 5 min</span>
              <h1 className="text-4xl md:text-6xl text-foreground mb-6 font-heading">« On voulait juste bouger un peu » : comment le CrossFit est devenu leur routine à deux</h1>
              <p className="text-lg text-foreground/85 leading-relaxed mb-8">
                Au départ, ils cherchaient surtout une activité commune. Pas forcément un objectif sportif énorme. Quelques mois plus tard, Thomas et Camille viennent trois fois par semaine — et ne s'imaginent plus s'arrêter. Voici leur histoire chez <strong>CrossFit Chistera</strong>.
              </p>
              <BlogCTA variant="hero" primaryLabel="Réserver notre essai à deux" />
            </header>

            <aside className="border-l-4 border-gold bg-charcoal/40 px-6 py-5 mb-12 rounded-r-md">
              <p className="text-sm uppercase tracking-wider text-gold font-heading mb-2">Témoignage</p>
              <p className="text-foreground/90 leading-relaxed italic">
                « Dans notre tête, le CrossFit, c'était des gens ultra musclés qui crient en soulevant des pneus. La réalité a été très différente. » — <strong className="not-italic">Thomas</strong>
              </p>
            </aside>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground">
              <h2>« On s'attendait à quelque chose de militaire »</h2>
              <p>
                Premier cours, premières surprises. Pas de cris, pas de jugement, pas de pression. Un coach qui prend le temps d'expliquer chaque mouvement, qui ajuste les charges en fonction du niveau de chacun. Thomas et Camille découvrent qu'ils peuvent faire le même WOD sans avoir le même niveau — c'est ça, la <strong>scalabilité</strong>.
              </p>

              <h2>Une routine qui s'est installée toute seule</h2>
              <p>
                Au bout de trois semaines, ils ont leurs créneaux préférés. Au bout de deux mois, ils planifient leur semaine autour de la box. « Avant, on essayait de se motiver pour aller à la salle. Maintenant, on a juste hâte de venir », résume Camille.
              </p>
              <p>
                Le secret n'est pas l'intensité — c'est le <strong>plaisir partagé</strong>, le sentiment de progresser ensemble, et la communauté qu'on retrouve à chaque cours.
              </p>
            </div>

            <section className="my-12">
              <div className="grid md:grid-cols-2 gap-4">
                {points.map((p) => {
                  const Icon = p.icon;
                  return (
                    <div key={p.title} className="border border-border bg-card rounded-lg p-5 hover:border-gold/50 transition-colors">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                        <h3 className="font-heading text-lg text-foreground">{p.title}</h3>
                      </div>
                      <p className="text-foreground/80 text-sm font-body normal-case">{p.desc}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            <BlogCTA single primaryLabel="Réserver mon essai gratuit" />

            <div className="my-16 border border-gold/30 bg-charcoal/40 rounded-lg px-6 py-8 text-center">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Et si vous testiez à deux ?</h2>
              <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">2 séances d'essai gratuites pour chacun. Réservez le même créneau, on s'occupe du reste.</p>
              <BlogCTA primaryLabel="Réserver notre essai à deux" />
            </div>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground">
              <h2>Pourquoi ça marche aussi bien à deux</h2>
              <p>
                S'inscrire en couple, c'est se donner mutuellement un cadre. Plus difficile de zapper une séance quand l'autre y va. Plus facile de tenir dans la durée quand on partage la fatigue, les progrès et les fous rires d'après-WOD. C'est aussi un excellent moyen de se reconnecter, sans téléphone, sans écran.
              </p>

              <h2>Une box accessible et conviviale dans l'Est lyonnais</h2>
              <p>
                CrossFit Chistera est une <strong>salle de CrossFit familiale</strong> située à Saint-Pierre-de-Chandieu. Beaucoup de couples nous rejoignent depuis Mions, Heyrieux, Toussieu, Chaponnay ou Saint-Bonnet-de-Mure. L'ambiance est volontairement bienveillante : on vient pour progresser, pas pour se comparer.
              </p>
            </div>

            <section className="my-16">
              <h2 className="text-3xl font-heading text-foreground mb-6">Questions fréquentes</h2>
              <div className="space-y-4">
                {faq.map((q) => (
                  <details key={q.name} className="group border border-border bg-card rounded-lg p-5 open:border-gold/50">
                    <summary className="cursor-pointer font-heading text-lg text-foreground flex justify-between items-center gap-4 list-none">
                      <span>{q.name}</span>
                      <ChevronRight className="w-5 h-5 text-gold shrink-0 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-foreground/85 leading-relaxed font-body normal-case">{q.acceptedAnswer.text}</p>
                  </details>
                ))}
              </div>
            </section>

            <section className="my-16">
              <div className="border border-gold/30 bg-gradient-to-br from-charcoal/60 to-charcoal/20 rounded-lg px-6 py-10 text-center">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Une nouvelle routine, à deux</h2>
                <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">Réservez votre créneau d'essai gratuit ou parlez d'abord à un coach.</p>
                <BlogCTA />
              </div>
            </section>

            <section className="my-12 border-t border-border pt-8">
              <h2 className="text-xl font-heading text-foreground mb-4">À lire aussi</h2>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm font-body normal-case">
                <li><Link to="/crossfit-debutant" className="text-gold hover:underline">→ CrossFit débutant</Link></li>
                <li><Link to="/reprendre-le-sport" className="text-gold hover:underline">→ Reprendre le sport</Link></li>
                <li><Link to="/blog/quest-ce-que-le-crossfit" className="text-gold hover:underline">→ C'est quoi le CrossFit ?</Link></li>
                <li><Link to="/cours/wod" className="text-gold hover:underline">→ Le cours WOD</Link></li>
              </ul>
            </section>
          </article>
        </main>
        <Footer />
        <BlogStickyCTA label="Réserver notre essai à deux" />
      </div>
    </>
  );
}
